import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, X, FileText, Image as ImageIcon, Video, Calendar as CalendarIcon, Loader2, Camera, StopCircle, Trash2, HardDrive, Plus, Mail, User, Edit3, Check, Shield, ChevronDown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { API } from "@/App";
import { pixel } from "@/lib/pixel";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { BizouLogo, KissIcon } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB standard tier
const MAX_RECORDING_SIZE = 48 * 1024 * 1024; // Auto-stop just below 50 MB
const EXTRA_SPACE_PRICE = 5;
const EXTRA_SPACE_SIZE = 100 * 1024 * 1024;

export default function CreateMessage({ user, logout }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const dateLocale = lang === "fr" ? frLocale : undefined;
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled");
  const editMessageId = searchParams.get("edit");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(!!editMessageId);
  const [dragActive, setDragActive] = useState(false);
  const [existingMessage, setExistingMessage] = useState(null);

  const [files, setFiles] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [extraSpaceUnits, setExtraSpaceUnits] = useState(0);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingSize, setRecordingSize] = useState(0);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDate, setVideoDate] = useState(new Date());

  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const liveVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.setAttribute('playsinline', '');
      node.setAttribute('webkit-playsinline', '');
      node.muted = true;
      const tryPlay = () => {
        node.play().catch(() => {
          setTimeout(() => node.play().catch(() => {}), 200);
        });
      };
      if (node.readyState >= 2) tryPlay();
      else node.onloadedmetadata = tryPlay;
    }
  }, [cameraPermission]);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    deliveryDate: null,
    recipients: [{ name: "", email: "" }],
    backupContactName: "",
    backupContactEmail: "",
    backupContactRelationship: "",
  });
  const [showBackupContact, setShowBackupContact] = useState(false);
  const isAdmin = Boolean(user?.is_admin);
  // Admin bizous default to "gift" mode: no Stripe, not counted in revenue.
  const [giftAsAdmin, setGiftAsAdmin] = useState(isAdmin);
  useEffect(() => { if (isAdmin) setGiftAsAdmin(true); }, [isAdmin]);
  const [expandedRecipients, setExpandedRecipients] = useState(new Set());
  const toggleRecipientDetails = (idx) => {
    setExpandedRecipients(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const updateRecipientField = (idx, field, value) => {
    const next = [...formData.recipients];
    next[idx] = { ...next[idx], [field]: value };
    setFormData({ ...formData, recipients: next });
  };
  const [sealAccepted, setSealAccepted] = useState(false);

  const totalAllowedSize = MAX_FILE_SIZE + (extraSpaceUnits * EXTRA_SPACE_SIZE);
  const getCurrentTotalSize = () => {
    let total = files.reduce((sum, f) => sum + f.size, 0);
    if (recordedBlob) total += recordedBlob.size;
    return total;
  };
  const currentTotalSize = getCurrentTotalSize();
  const usagePercentage = Math.min((currentTotalSize / totalAllowedSize) * 100, 100);
  const isOverLimit = currentTotalSize > totalAllowedSize;
  const remainingSpace = totalAllowedSize - currentTotalSize;

  useEffect(() => {
    if (!editMessageId) return;
    const loadMessage = async () => {
      try {
        const response = await fetch(`${API}/messages/${editMessageId}`, { credentials: "include" });
        if (!response.ok) throw new Error();
        const msg = await response.json();
        if (msg.status === "scheduled" || (msg.status === "paid" && !msg.is_reservation)) {
          toast.error(t("dashboard.sealedMessage"));
          navigate("/dashboard");
          return;
        }
        setExistingMessage(msg);
        setFormData({
          title: msg.title || "",
          content: msg.content || "",
          deliveryDate: msg.delivery_date ? new Date(msg.delivery_date) : null,
          recipients: (msg.recipients && msg.recipients.length > 0)
            ? msg.recipients.map(r => ({
                name: r.name || "",
                email: r.email || "",
                last_name: r.last_name || "",
                phone: r.phone || "",
                birth_date: r.birth_date || "",
                secondary_email: r.secondary_email || "",
                relationship: r.relationship || "",
                trusted_person_name: r.trusted_person_name || "",
                trusted_person_email: r.trusted_person_email || "",
              }))
            : [{ name: msg.recipient_name || "", email: msg.recipient_email || "" }],
          backupContactName: msg.backup_contact?.name || "",
          backupContactEmail: msg.backup_contact?.email || "",
          backupContactRelationship: msg.backup_contact?.relationship || "",
        });
        if (msg.backup_contact?.email) setShowBackupContact(true);
        if (msg.files && msg.files.length > 0) {
          setFiles(msg.files.map(f => ({
            id: f.file_id, fileId: f.file_id, type: f.file_type || "file",
            name: f.file_name, size: f.file_size || 0, isExisting: true,
          })));
        }
      } catch {
        toast.error(t("toast.errorLoadMessage"));
        navigate("/dashboard");
      } finally {
        setLoadingMessage(false);
      }
    };
    loadMessage();
  }, [editMessageId, navigate]);

  useEffect(() => {
    if (cancelled) toast.error(t("toast.paymentCancelled"));
  }, [cancelled]);

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [recordedUrl]);

  const requestCameraAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
        audio: true
      });
      streamRef.current = stream;
      setCameraPermission("granted");
      toast.success(t("toast.cameraReady"));
    } catch (error) {
      setCameraPermission("denied");
      if (error.name === "NotAllowedError") toast.error(t("toast.cameraDenied"));
      else if (error.name === "NotFoundError") toast.error(t("toast.cameraNotFound"));
      else toast.error(t("toast.cameraError") + " " + error.message);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    let mimeType = 'video/webm';
    for (const codec of ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp8', 'video/webm']) {
      if (MediaRecorder.isTypeSupported(codec)) { mimeType = codec; break; }
    }
    const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 1200000 });
    let accumulatedSize = 0;
    let autoStopped = false;
    mediaRecorder.ondataavailable = (e) => {
      if (e.data?.size > 0) {
        chunksRef.current.push(e.data);
        accumulatedSize += e.data.size;
        setRecordingSize(accumulatedSize);
        if (accumulatedSize >= MAX_RECORDING_SIZE && !autoStopped) {
          autoStopped = true;
          try { mediaRecorder.stop(); } catch (_) {}
          setIsRecording(false);
          if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
          toast.info(t("toast.maxSizeReached"));
        }
      }
    };
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      if (!videoTitle) {
        const dateStr = format(new Date(), "d MMMM yyyy", { locale: dateLocale });
        setVideoTitle(lang === "fr" ? `Vidéo du ${dateStr}` : `Video from ${dateStr}`);
      }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    };
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingSize(0);
    setVideoDate(new Date());
    // Visual elapsed timer only (no time-based auto-stop)
    timerRef.current = setInterval(() => {
      setRecordingTime(p => p + 1);
    }, 1000);
    toast.success(t("toast.recordingStarted"));
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      toast.success(t("toast.recordingStopped"));
    }
  };

  const addRecordedVideoToFiles = () => {
    if (!recordedBlob) return;
    setFiles(prev => [...prev, {
      id: `video_${Date.now()}`,
      file: new File([recordedBlob], `${videoTitle || 'video-message'}.webm`, { type: 'video/webm' }),
      type: 'video',
      name: `${videoTitle || (lang === "fr" ? "Vidéo enregistrée" : "Recorded video")}.webm`,
      size: recordedBlob.size,
      isExisting: false,
    }]);
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setVideoTitle("");
    setCameraPermission(null);
    toast.success(t("toast.videoAdded"));
  };

  const deleteRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setRecordingSize(0);
    setCameraPermission(null);
    setVideoTitle("");
  };

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const fmtSize = (bytes) => bytes === 0 ? "0 Mo" : lang === "fr" ? `${(bytes / (1024*1024)).toFixed(2)} Mo` : `${(bytes / (1024*1024)).toFixed(2)} MB`;

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === "dragenter" || e.type === "dragover"); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files) handleFilesSelect(Array.from(e.dataTransfer.files)); };

  const handleFilesSelect = (selectedFiles) => {
    const newFiles = [];
    let runningSize = currentTotalSize;
    for (const file of selectedFiles) {
      if (runningSize + file.size > totalAllowedSize) { toast.error(t("toast.fileExceedsSpace", { name: file.name })); continue; }
      let type = 'file';
      if (file.type.startsWith("video/")) type = 'video';
      else if (file.type.startsWith("image/")) type = 'photo';
      else if (file.type.startsWith("text/")) type = 'text';
      newFiles.push({ id: `file_${Date.now()}_${Math.random().toString(36).substr(2,9)}`, file, type, name: file.name, size: file.size, isExisting: false });
      runningSize += file.size;
    }
    if (newFiles.length > 0) { setFiles(prev => [...prev, ...newFiles]); toast.success(t("toast.filesAdded", { count: newFiles.length })); }
  };

  const removeFile = async (fileItem) => {
    if (fileItem.isExisting && fileItem.fileId && editMessageId) {
      try {
        const response = await fetch(`${API}/messages/${editMessageId}/file?file_id=${fileItem.fileId}`, { method: "DELETE", credentials: "include" });
        if (!response.ok) throw new Error();
        setRemovedFileIds(prev => [...prev, fileItem.fileId]);
      } catch {
        toast.error(t("toast.fileDeleteError"));
        return;
      }
    }
    setFiles(prev => prev.filter(f => f.id !== fileItem.id));
    toast.success(t("toast.fileDeleted"));
  };

  const addExtraSpace = () => { setExtraSpaceUnits(p => p + 1); toast.success(t("toast.extraSpaceAdded", { price: EXTRA_SPACE_PRICE })); };

  const validateStep1 = () => {
    if (!formData.content.trim()) { toast.error(t("toast.validationContent")); return false; }
    if (isOverLimit) { toast.error(t("toast.validationOverLimit")); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.deliveryDate) { toast.error(t("toast.validationDate")); return false; }
    const recipients = formData.recipients || [];
    const validRecipients = recipients.filter(r => r.email.trim() && r.name.trim());
    if (validRecipients.length === 0) { toast.error(t("toast.validationRecipientRequired")); return false; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const r of validRecipients) {
      if (!emailRe.test(r.email.trim())) {
        toast.error(t("toast.validationEmailFormat") + ` (${r.email})`);
        return false;
      }
    }
    const lowered = validRecipients.map(r => r.email.trim().toLowerCase());
    if (new Set(lowered).size !== lowered.length) {
      toast.error(t("toast.validationDuplicateRecipient"));
      return false;
    }
    if (validRecipients.length > 10) {
      toast.error(t("toast.validationMaxRecipients"));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", formData.title);
      fd.append("content", formData.content);
      fd.append("delivery_date", formData.deliveryDate.toISOString());
      const validRecipients = (formData.recipients || [])
        .filter(r => r.email.trim() && r.name.trim())
        .map(r => {
          const out = { name: r.name.trim(), email: r.email.trim().toLowerCase() };
          const optional = ["last_name", "phone", "birth_date", "secondary_email", "relationship", "trusted_person_name", "trusted_person_email"];
          for (const k of optional) {
            const v = (r[k] || "").trim();
            if (v) out[k] = v;
          }
          return out;
        });
      fd.append("recipients_json", JSON.stringify(validRecipients));
      fd.append("recipient_email", validRecipients[0]?.email || "");
      fd.append("recipient_name", validRecipients[0]?.name || "");
      if (formData.backupContactEmail) {
        fd.append("backup_contact_name", formData.backupContactName);
        fd.append("backup_contact_email", formData.backupContactEmail);
        fd.append("backup_contact_relationship", formData.backupContactRelationship);
      }
      let messageType = "text";
      if (files.some(f => f.type === "video")) messageType = "video";
      else if (files.some(f => f.type === "photo")) messageType = "photo";
      fd.append("message_type", messageType);
      const newFiles = files.filter(f => !f.isExisting && f.file);
      newFiles.forEach(f => fd.append("files", f.file));

      if (editMessageId) {
        const response = await fetch(`${API}/messages/${editMessageId}`, { method: "PUT", credentials: "include", body: fd });
        if (!response.ok) { const error = await response.json(); throw new Error(error.detail || t("toast.genericError")); }
        const updatedMsg = await response.json();
        
        // Admin-only bypass: skip Stripe and mark as gifted
        if (isAdmin && giftAsAdmin && (updatedMsg.status === "pending_payment" || updatedMsg.status === "draft" || updatedMsg.status === "pending_upgrade")) {
          const giftResp = await fetch(`${API}/admin/messages/${editMessageId}/gift`, { method: "POST", credentials: "include" });
          if (!giftResp.ok) { const error = await giftResp.json(); throw new Error(error.detail || t("toast.genericError")); }
          toast.success(t("toast.giftedSuccess") || "Bizou cadeau créé");
          navigate("/dashboard");
          return;
        }
        // If message needs payment (new or upgrade)
        if (updatedMsg.status === "pending_payment" || updatedMsg.status === "draft" || updatedMsg.status === "pending_upgrade") {
          const checkoutResponse = await fetch(`${API}/payment/checkout`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message_id: editMessageId, origin_url: window.location.origin, extra_space_units: extraSpaceUnits }),
          });
          if (!checkoutResponse.ok) { const error = await checkoutResponse.json(); throw new Error(error.detail || t("toast.genericError")); }
          const checkoutData = await checkoutResponse.json();
          if (checkoutData.url) {
            pixel.initiateCheckout({ price: updatedMsg.upgrade_diff || totalPrice, messageId: editMessageId });
            window.location.href = checkoutData.url;
          } else {
            toast.success(t("toast.messageModified"));
            navigate("/dashboard");
          }
        } else {
          toast.success(t("toast.messageModified"));
          navigate("/dashboard");
        }
      } else {
        const response = await fetch(`${API}/messages`, { method: "POST", credentials: "include", body: fd });
        if (!response.ok) { const error = await response.json(); throw new Error(error.detail || t("toast.genericError")); }
        const data = await response.json();
        // Admin-only bypass: skip Stripe and mark as gifted
        if (isAdmin && giftAsAdmin) {
          const giftResp = await fetch(`${API}/admin/messages/${data.message_id}/gift`, { method: "POST", credentials: "include" });
          if (!giftResp.ok) { const error = await giftResp.json(); throw new Error(error.detail || t("toast.genericError")); }
          toast.success(t("toast.giftedSuccess") || "Bizou cadeau créé");
          navigate("/dashboard");
          return;
        }
        const checkoutResponse = await fetch(`${API}/payment/checkout`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message_id: data.message_id, origin_url: window.location.origin, extra_space_units: extraSpaceUnits }),
        });
        if (!checkoutResponse.ok) { const error = await checkoutResponse.json(); throw new Error(error.detail || t("toast.genericError")); }
        const checkoutData = await checkoutResponse.json();
        pixel.initiateCheckout({ price: totalPrice, messageId: data.message_id });
        window.location.href = checkoutData.url;
      }
    } catch (error) {
      toast.error(error.message || t("toast.genericError"));
      setLoading(false);
    }
  };

  const PRICING_TIERS = [
    { maxYears: 10, price: 29, label: t("create.tierLabel10"), color: "bg-blue-50 border-blue-200 text-blue-700" },
    { maxYears: 20, price: 49, label: t("create.tierLabel20"), color: "bg-violet-50 border-violet-200 text-violet-700" },
    { maxYears: 30, price: 79, label: t("create.tierLabel30"), color: "bg-[#FDF0F4] border-[#E8175D]/20 text-[#E8175D]" },
  ];

  const getYearsAhead = () => {
    if (!formData.deliveryDate) return 0;
    return Math.max(0, (formData.deliveryDate - new Date()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const yearsAhead = getYearsAhead();
  const activeTier = PRICING_TIERS.find(tier => yearsAhead <= tier.maxYears) || PRICING_TIERS[PRICING_TIERS.length - 1];
  const basePrice = activeTier.price;
  const extraSpaceCost = extraSpaceUnits * EXTRA_SPACE_PRICE;
  const totalPrice = basePrice + extraSpaceCost;

  // Upgrade calculation for edit mode
  const isEditMode = !!editMessageId;
  const amountPaid = existingMessage?.amount_paid || 0;
  const isUpgrade = isEditMode && amountPaid > 0 && basePrice > amountPaid;
  const maxDate = new Date();
  const isDowngrade = isEditMode && amountPaid > 0 && basePrice < amountPaid;
  const upgradeDiff = isUpgrade ? Math.round((basePrice - amountPaid) * 100) / 100 : 0;
  const editTotalPrice = isUpgrade ? upgradeDiff + extraSpaceCost : extraSpaceCost > 0 ? extraSpaceCost : 0;
  const needsPayment = !editMessageId || isUpgrade || existingMessage?.status === "pending_payment" || existingMessage?.status === "pending_upgrade";
  const isReservedEdit = existingMessage?.status === "reserved" && existingMessage?.is_reservation;
  const needsSealConfirmation = needsPayment || isReservedEdit;
  maxDate.setFullYear(maxDate.getFullYear() + 30);

  const getFileIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4 text-[#E8175D]" />;
      case 'photo': return <ImageIcon className="w-4 h-4 text-[#E8175D]" />;
      default: return <FileText className="w-4 h-4 text-[#E8175D]" />;
    }
  };

  const getDeliveryText = () => {
    if (yearsAhead < 1) {
      const months = Math.max(1, Math.ceil(yearsAhead * 12));
      return `${t("create.deliveryIn")} ${months} ${t("create.months")}`;
    }
    const yrs = Math.round(yearsAhead);
    return `${t("create.deliveryIn")} ${yrs} ${yrs > 1 ? t("create.years") : t("create.year")}`;
  };

  if (loadingMessage) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-[#78716C]">{t("create.loadingMessage")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="container-bizou flex items-center justify-between h-16">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#78716C] hover:text-[#1C1917]" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden md:inline">{t("common.back")}</span>
          </button>
          <a href="/" className="flex items-center"><BizouLogo size="md" /></a>
          <div className="w-20"></div>
        </div>
      </header>

      <div className="bg-white border-b border-[#F0E8EB]">
        <div className="container-bizou py-4">
          <div className="flex items-center gap-4">
            <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-[#E8175D]' : 'bg-[#E7E5E4]'}`}></div>
            <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-[#E8175D]' : 'bg-[#E7E5E4]'}`}></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-[#78716C]">
            <span className={step === 1 ? 'text-[#E8175D] font-medium' : ''}>{t("create.stepMessage")}</span>
            <span className={step === 2 ? 'text-[#E8175D] font-medium' : ''}>{t("create.stepRecipient")}</span>
          </div>
        </div>
      </div>

      <main className="container-bizou py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          {step === 1 ? (
            <div className="animate-fade-in" data-testid="step-1-form">
              <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {editMessageId ? t("create.titleEdit") : t("create.titleNew")}
              </h1>
              <p className="text-[#78716C] mb-8">
                {editMessageId ? t("create.editDesc") : t("create.newDesc")}
              </p>

              <div className="mb-6">
                <Label htmlFor="title" className="label-bizou">{t("create.titleLabel")} <span className="text-[#78716C] font-normal">({t("create.titleOptional")})</span></Label>
                <Input id="title" placeholder={t("create.titlePlaceholder")} className="input-bizou" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="title-input" />
              </div>

              <div className="mb-6">
                <Label htmlFor="content" className="label-bizou">{t("create.contentLabel")} <span className="text-[#E8175D]">*</span></Label>
                <Textarea id="content" placeholder={t("create.contentPlaceholder")} className="textarea-bizou min-h-[150px]" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} data-testid="content-textarea" />
              </div>

              {/* === VIDEO RECORDING — Primary section === */}
              <div className="mb-8" data-testid="video-section">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center">
                    <Camera className="w-4 h-4 text-[#E8175D]" />
                  </div>
                  <div>
                    <Label className="label-bizou block">{t("create.videoSectionTitle")}</Label>
                  </div>
                </div>
                <p className="text-sm text-[#78716C] mb-2 ml-11">{t("create.videoSectionSubtitle")}</p>
                <p className="text-xs text-[#78716C] mb-4 ml-11 italic">{t("create.videoSizeLimit")}</p>

                <div className="rounded-2xl border-2 border-dashed border-[#E8175D]/20 bg-[#FDF0F4]/30 p-4" data-testid="video-record-section">
                  {!cameraPermission && !recordedUrl && (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 rounded-full bg-[#E8175D]/10 flex items-center justify-center mx-auto mb-4">
                        <Camera className="w-8 h-8 text-[#E8175D]" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-medium text-[#1C1917] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t("create.recordTitle")}</h3>
                      <p className="text-[#78716C] mb-5 text-sm">{t("create.recordDesc")}</p>
                      <Button onClick={requestCameraAccess} className="btn-primary" data-testid="request-camera-btn">
                        <Camera className="w-5 h-5 mr-2" />{t("create.authorizeCamera")}
                      </Button>
                    </div>
                  )}
                  {cameraPermission === "denied" && (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <Camera className="w-8 h-8 text-red-500" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-medium text-[#1C1917] mb-2">{t("create.cameraDenied")}</h3>
                      <p className="text-[#78716C] mb-4 text-sm">{t("create.cameraDeniedDesc")}</p>
                      <Button onClick={requestCameraAccess} variant="outline" className="btn-secondary">{t("create.retry")}</Button>
                    </div>
                  )}
                  {cameraPermission === "granted" && !recordedUrl && (
                    <div>
                      <div className="relative rounded-2xl overflow-hidden bg-[#1C1917] mb-4">
                        <video ref={liveVideoRef} autoPlay muted playsInline webkit-playsinline="true" className="w-full aspect-video object-cover" style={{ transform: 'scaleX(-1)', minHeight: '200px', backgroundColor: '#1C1917' }} data-testid="video-preview" />
                        {isRecording && (
                          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm" data-testid="recording-indicator">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                            REC {formatTime(recordingTime)} · {fmtSize(recordingSize)}
                          </div>
                        )}
                        {!isRecording && (
                          <div className="absolute bottom-4 left-4 text-white/70 text-sm flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>{t("create.cameraActive")}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-center gap-4">
                        {!isRecording ? (
                          <Button onClick={startRecording} className="btn-primary" data-testid="start-recording-btn">
                            <Camera className="w-5 h-5 mr-2" />{t("create.startRecording")}
                          </Button>
                        ) : (
                          <Button onClick={stopRecording} className="bg-red-500 hover:bg-red-600 text-white h-12 px-8 rounded-full" data-testid="stop-recording-btn">
                            <StopCircle className="w-5 h-5 mr-2" />{t("create.stopRecording")} ({formatTime(recordingTime)})
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  {recordedUrl && (
                    <div data-testid="recorded-video-preview">
                      <div className="mb-4">
                        <Label htmlFor="videoTitle" className="label-bizou">{t("create.videoTitleLabel")}</Label>
                        <Input id="videoTitle" placeholder={t("create.videoTitlePlaceholder")} className="input-bizou" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} data-testid="video-title-input" />
                      </div>
                      <div className="mb-4 p-3 bg-[#F2EFE9] rounded-xl flex items-center gap-3">
                        <CalendarIcon className="w-5 h-5 text-[#E8175D]" />
                        <span className="text-sm text-[#78716C]">{t("create.recordedOn", { date: format(videoDate, lang === "fr" ? "d MMMM yyyy 'à' HH:mm" : "MMMM d, yyyy 'at' HH:mm", { locale: dateLocale }) })}</span>
                      </div>
                      <div className="relative rounded-2xl overflow-hidden bg-[#1C1917] mb-4">
                        <video ref={previewVideoRef} src={recordedUrl} controls playsInline preload="auto" className="w-full aspect-video" style={{ backgroundColor: '#1C1917' }} data-testid="recorded-video-player" />
                      </div>
                      {recordedBlob && (
                        <div className="mb-4 p-3 bg-[#F2EFE9] rounded-xl flex items-center gap-3">
                          <HardDrive className="w-5 h-5 text-[#E8175D]" />
                          <span className="text-sm text-[#78716C]">{t("create.size")} {fmtSize(recordedBlob.size)}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap justify-center gap-3">
                        <Button onClick={deleteRecording} variant="outline" className="btn-secondary" data-testid="delete-recording-btn">
                          <Trash2 className="w-5 h-5 mr-2" />{t("create.deleteRecording")}
                        </Button>
                        <Button onClick={addRecordedVideoToFiles} className="btn-primary" data-testid="add-video-btn">
                          <Plus className="w-5 h-5 mr-2" />{t("create.addToFiles")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* === EXISTING FILES === */}
              {files.length > 0 && (
                <div className="mb-6">
                  <Label className="label-bizou mb-3 block">{t("create.filesCount", { count: files.length })}</Label>
                  <div className="space-y-2">
                    {files.map((f) => (
                      <div key={f.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#E7E5E4]" data-testid={`file-item-${f.id}`}>
                        <div className="w-10 h-10 rounded-lg bg-[#F2EFE9] flex items-center justify-center">{getFileIcon(f.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1C1917] text-sm truncate">{f.name}</p>
                          <p className="text-xs text-[#78716C]">
                            {fmtSize(f.size)} {f.isExisting && <span className="text-[#84A98C]">• {t("create.existing")}</span>}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeFile(f)} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`remove-file-${f.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* === FILE UPLOAD — Secondary section === */}
              <div className="mb-6">
                <Label className="label-bizou mb-4 block">{t("create.addMediaLabel")} <span className="text-[#78716C] font-normal">({t("create.addMediaOptional")})</span></Label>
                <div className={`upload-zone ${dragActive ? 'dragging' : ''}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => document.getElementById('file-input').click()} data-testid="upload-zone">
                  <input id="file-input" type="file" accept="image/*,video/*,text/*,.pdf,.doc,.docx" multiple className="hidden" onChange={(e) => e.target.files && handleFilesSelect(Array.from(e.target.files))} />
                  <Upload className="w-8 h-8 text-[#78716C] mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-[#1C1917] font-medium mb-1 text-sm">{t("create.dragOrClick")}</p>
                  <p className="text-xs text-[#78716C]">{t("create.fileTypes")} • {t("create.remainingSpace")} {fmtSize(Math.max(0, remainingSpace))}</p>
                </div>
              </div>

              {(files.length > 0 || recordedBlob) && (
                <div className="mb-8 p-4 bg-white rounded-2xl border border-[#E7E5E4]" data-testid="storage-indicator">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-5 h-5 text-[#E8175D]" />
                      <span className="font-medium text-[#1C1917]">{t("create.spaceUsed")}</span>
                    </div>
                    <span className="text-sm text-[#78716C]">{fmtSize(currentTotalSize)} / {fmtSize(totalAllowedSize)}</span>
                  </div>
                  <Progress value={usagePercentage} className={`h-3 mb-3 ${isOverLimit ? '[&>div]:bg-red-500' : '[&>div]:bg-[#E8175D]'}`} />
                  {isOverLimit ? (
                    <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                      <p className="text-red-600 text-sm">{t("create.limitExceeded")}</p>
                    </div>
                  ) : remainingSpace < 20 * 1024 * 1024 && (
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-amber-700 text-sm">{t("create.lowSpace")}</p>
                    </div>
                  )}
                </div>
              )}

              <Button onClick={handleNext} className="btn-primary w-full" disabled={isOverLimit} data-testid="next-btn">
                {t("create.continue")}
              </Button>
            </div>
          ) : (
            <div className="animate-fade-in" data-testid="step-2-form">
              <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("create.recipientStepTitle")}
              </h1>
              <p className="text-[#78716C] mb-8">{t("create.recipientStepDesc")}</p>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-bizou !mb-0">{t("create.recipientsLabel")} <span className="text-[#E8175D]">*</span></Label>
                  <span className="text-xs text-[#78716C]">{(formData.recipients || []).length}/10</span>
                </div>
                <div className="space-y-3" data-testid="recipients-list">
                  {(formData.recipients || []).map((r, idx) => {
                    const isExpanded = expandedRecipients.has(idx);
                    return (
                    <div key={idx} className="bg-white border border-[#F0E8EB] rounded-2xl p-3" data-testid={`recipient-row-${idx}`}>
                      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                        <Input
                          placeholder={t("create.recipientNamePlaceholder")}
                          className="input-bizou !mb-0 flex-1"
                          value={r.name}
                          onChange={(e) => updateRecipientField(idx, "name", e.target.value)}
                          data-testid={`recipient-name-input-${idx}`}
                        />
                        <Input
                          type="email"
                          placeholder={t("create.recipientEmailPlaceholder")}
                          className="input-bizou !mb-0 flex-1"
                          value={r.email}
                          onChange={(e) => updateRecipientField(idx, "email", e.target.value)}
                          data-testid={`recipient-email-input-${idx}`}
                        />
                        {(formData.recipients || []).length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const next = formData.recipients.filter((_, i) => i !== idx);
                              setFormData({ ...formData, recipients: next });
                              setExpandedRecipients(prev => {
                                const n = new Set();
                                prev.forEach(i => { if (i < idx) n.add(i); else if (i > idx) n.add(i - 1); });
                                return n;
                              });
                            }}
                            className="text-[#78716C] hover:text-red-500 hover:bg-red-50 shrink-0"
                            data-testid={`recipient-remove-${idx}`}
                            aria-label={t("create.recipientRemove")}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleRecipientDetails(idx)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-[#E8175D] hover:text-[#C4134F] font-medium"
                        data-testid={`recipient-toggle-optional-${idx}`}
                      >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        {t("create.recipientOptionalToggle")}
                      </button>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-[#F0E8EB] space-y-3" data-testid={`recipient-optional-block-${idx}`}>
                          <div className="flex items-start gap-2 bg-[#FDF8F4] rounded-lg p-3">
                            <Lock className="w-3.5 h-3.5 text-[#E8175D] mt-0.5 shrink-0" />
                            <p className="text-xs text-[#57534e] leading-relaxed">{t("create.recipientOptionalIntro")}</p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-[#78716C] mb-1 block">{t("create.recipientLastName")}</Label>
                              <Input
                                placeholder={t("create.recipientLastNamePlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.last_name || ""}
                                onChange={(e) => updateRecipientField(idx, "last_name", e.target.value)}
                                data-testid={`recipient-lastname-input-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#78716C] mb-1 block">{t("create.recipientPhone")}</Label>
                              <Input
                                type="tel"
                                placeholder={t("create.recipientPhonePlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.phone || ""}
                                onChange={(e) => updateRecipientField(idx, "phone", e.target.value)}
                                data-testid={`recipient-phone-input-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#78716C] mb-1 block">{t("create.recipientBirthDate")}</Label>
                              <Input
                                type="date"
                                className="input-bizou !mb-0"
                                value={r.birth_date || ""}
                                onChange={(e) => updateRecipientField(idx, "birth_date", e.target.value)}
                                data-testid={`recipient-birthdate-input-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#78716C] mb-1 block">{t("create.recipientSecondaryEmail")}</Label>
                              <Input
                                type="email"
                                placeholder={t("create.recipientSecondaryEmailPlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.secondary_email || ""}
                                onChange={(e) => updateRecipientField(idx, "secondary_email", e.target.value)}
                                data-testid={`recipient-secondaryemail-input-${idx}`}
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <Label className="text-xs text-[#78716C] mb-1 block">{t("create.recipientRelationship")}</Label>
                              <Input
                                placeholder={t("create.recipientRelationshipPlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.relationship || ""}
                                onChange={(e) => updateRecipientField(idx, "relationship", e.target.value)}
                                data-testid={`recipient-relationship-input-${idx}`}
                              />
                            </div>
                          </div>

                          <div className="bg-[#FDF8F4] rounded-lg p-3 space-y-2">
                            <p className="text-xs font-medium text-[#1c1917]">{t("create.recipientTrustedPersonTitle")}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <Input
                                placeholder={t("create.recipientTrustedPersonNamePlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.trusted_person_name || ""}
                                onChange={(e) => updateRecipientField(idx, "trusted_person_name", e.target.value)}
                                data-testid={`recipient-trustedname-input-${idx}`}
                              />
                              <Input
                                type="email"
                                placeholder={t("create.recipientTrustedPersonEmailPlaceholder")}
                                className="input-bizou !mb-0"
                                value={r.trusted_person_email || ""}
                                onChange={(e) => updateRecipientField(idx, "trusted_person_email", e.target.value)}
                                data-testid={`recipient-trustedemail-input-${idx}`}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                {(formData.recipients || []).length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, recipients: [...(formData.recipients || []), { name: "", email: "" }] })}
                    className="mt-3 h-10 rounded-full border-dashed border-[#E8175D]/40 text-[#E8175D] hover:bg-[#FDF0F4]"
                    data-testid="recipient-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />{t("create.recipientAdd")}
                  </Button>
                )}
                <p className="text-xs text-[#78716C] mt-3" data-testid="rgpd-recipient-notice">{t("create.recipientsHelp")}</p>
                <p className="text-xs text-[#78716C] mt-1">{t("create.rgpdNotice")}</p>
              </div>

              <div className="mb-8">
                <Label className="label-bizou">{t("create.deliveryDate")} <span className="text-[#E8175D]">*</span></Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="date"
                    className="input-bizou flex-1"
                    value={formData.deliveryDate ? format(formData.deliveryDate, "yyyy-MM-dd") : ""}
                    min={format(new Date(), "yyyy-MM-dd")}
                    max={format(maxDate, "yyyy-MM-dd")}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        const d = new Date(val + "T10:00:00");
                        if (!isNaN(d.getTime()) && d <= maxDate) setFormData({ ...formData, deliveryDate: d });
                      } else {
                        setFormData({ ...formData, deliveryDate: null });
                      }
                    }}
                    data-testid="date-input"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="shrink-0 h-10 w-10 border-stone-200" data-testid="date-picker-btn">
                        <CalendarIcon className="h-4 w-4 text-[#78716C]" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white shadow-xl border border-stone-200 rounded-xl z-50" align="end">
                      <Calendar mode="single" selected={formData.deliveryDate} onSelect={(date) => { if (date && date <= maxDate) setFormData({ ...formData, deliveryDate: date }); }} disabled={(date) => date < new Date() || date > maxDate} initialFocus locale={lang === "fr" ? frLocale : undefined} data-testid="calendar" />
                    </PopoverContent>
                  </Popover>
                </div>
                {formData.deliveryDate && (
                  <p className="text-xs text-[#78716C] mt-1.5">{format(formData.deliveryDate, "EEEE d MMMM yyyy", { locale: dateLocale })}</p>
                )}

                {formData.deliveryDate && (
                  <div className={`mt-4 p-4 rounded-xl border ${activeTier.color} transition-all`} data-testid="price-preview">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold">{getDeliveryText()}</p>
                        <p className="text-xs opacity-70 mt-0.5">{t("create.secureStorage")}</p>
                      </div>
                      <p className="text-2xl font-bold">{basePrice}€</p>
                    </div>
                    <div className="flex gap-2" data-testid="pricing-tiers">
                      {PRICING_TIERS.map((tier) => (
                        <div
                          key={tier.maxYears}
                          className={`flex-1 text-center py-2 px-2 rounded-lg text-xs font-medium border transition-all ${
                            activeTier.maxYears === tier.maxYears
                              ? "bg-white shadow-sm border-current scale-[1.02]"
                              : "bg-white/40 border-transparent opacity-50"
                          }`}
                        >
                          <p className="font-bold">{tier.price}€</p>
                          <p className="opacity-70">{tier.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-8">
                <button
                  type="button"
                  onClick={() => setShowBackupContact(!showBackupContact)}
                  className="flex items-center gap-2 text-sm text-[#78716C] hover:text-[#E8175D] transition-colors"
                  data-testid="toggle-backup-contact"
                >
                  <Shield className="w-4 h-4" />
                  <span>{t("create.backupContact")}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showBackupContact ? "rotate-180" : ""}`} />
                </button>

                {showBackupContact && (
                  <div className="mt-4 p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-4 animate-fade-in" data-testid="backup-contact-section">
                    <p className="text-xs text-[#78716C] leading-relaxed">{t("create.backupExplain")}</p>
                    <div>
                      <Label htmlFor="backupName" className="label-bizou">{t("create.backupName")}</Label>
                      <Input id="backupName" placeholder={t("create.backupNamePlaceholder")} className="input-bizou" value={formData.backupContactName} onChange={(e) => setFormData({ ...formData, backupContactName: e.target.value })} data-testid="backup-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="backupEmail" className="label-bizou">{t("create.backupEmail")}</Label>
                      <Input id="backupEmail" type="email" placeholder={lang === "fr" ? "jean@exemple.com" : "john@example.com"} className="input-bizou" value={formData.backupContactEmail} onChange={(e) => setFormData({ ...formData, backupContactEmail: e.target.value })} data-testid="backup-email-input" />
                    </div>
                    <div>
                      <Label htmlFor="backupRelationship" className="label-bizou">{t("create.backupRelationship")}</Label>
                      <Input id="backupRelationship" placeholder={t("create.backupRelationshipPlaceholder")} className="input-bizou" value={formData.backupContactRelationship} onChange={(e) => setFormData({ ...formData, backupContactRelationship: e.target.value })} data-testid="backup-relationship-input" />
                    </div>
                  </div>
                )}
              </div>

              <div className="card-feature mb-8" data-testid="summary-card">
                <h3 className="font-medium text-[#1C1917] mb-6 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{t("create.summary")}</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-[#E8175D]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#78716C] mb-1">{t("create.summaryTitle")}</p>
                      <p className="text-[#1C1917] font-medium truncate">{formData.title || t("common.noTitle")}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-[#78716C] hover:text-[#E8175D]"><Edit3 className="w-4 h-4" /></Button>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-[#E8175D]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#78716C] mb-1">{t("create.summaryTextContent")}</p>
                      <p className="text-[#1C1917] text-sm line-clamp-2">{formData.content || "-"}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-[#78716C] hover:text-[#E8175D]"><Edit3 className="w-4 h-4" /></Button>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0"><Upload className="w-4 h-4 text-[#E8175D]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#78716C] mb-1">{t("create.summaryAttachedFiles", { count: files.length })}</p>
                      {files.length > 0 ? (
                        <div className="space-y-1">
                          {files.map(f => (
                            <div key={f.id} className="flex items-center gap-2 text-sm">
                              {getFileIcon(f.type)}
                              <span className="text-[#1C1917] truncate flex-1">{f.name}</span>
                              <span className="text-[#78716C] text-xs">{fmtSize(f.size)}</span>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-[#78716C] italic">{t("create.summaryNoFiles")}</p>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-[#E8175D] hover:text-[#C4134F]"><Plus className="w-4 h-4" /></Button>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-[#E8175D]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#78716C] mb-1">
                        {t("create.summaryRecipient")}
                        {(formData.recipients || []).filter(r => r.email && r.name).length > 1 && (
                          <span className="ml-2 inline-flex items-center text-[#E8175D] font-medium">
                            ×{(formData.recipients || []).filter(r => r.email && r.name).length}
                          </span>
                        )}
                      </p>
                      <div className="space-y-1" data-testid="summary-recipients">
                        {(formData.recipients || []).filter(r => r.email || r.name).map((r, i) => (
                          <div key={i} className="text-sm">
                            <span className="text-[#1C1917] font-medium">{r.name || "-"}</span>
                            <span className="text-[#78716C] text-xs ml-2">{r.email || "-"}</span>
                          </div>
                        ))}
                        {(formData.recipients || []).filter(r => r.email || r.name).length === 0 && (
                          <p className="text-[#78716C] italic text-sm">-</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {formData.backupContactEmail && (
                    <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                      <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0"><Shield className="w-4 h-4 text-stone-500" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#78716C] mb-1">{t("create.summaryBackup")}</p>
                        <p className="text-[#1C1917] font-medium">{formData.backupContactName || "-"}</p>
                        <p className="text-xs text-[#78716C]">{formData.backupContactEmail}{formData.backupContactRelationship ? ` (${formData.backupContactRelationship})` : ""}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-[#E8175D]/10 flex items-center justify-center flex-shrink-0"><CalendarIcon className="w-4 h-4 text-[#E8175D]" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#78716C] mb-1">{t("create.summaryDelivery")}</p>
                      <p className="text-[#1C1917] font-medium">{formData.deliveryDate ? format(formData.deliveryDate, "EEEE d MMMM yyyy", { locale: dateLocale }) : "-"}</p>
                    </div>
                  </div>
                </div>

                {files.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-[#E7E5E4]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2"><HardDrive className="w-4 h-4 text-[#78716C]" /><span className="text-sm text-[#78716C]">{t("create.spaceUsed")}</span></div>
                      <span className="text-sm text-[#1C1917] font-medium">{fmtSize(currentTotalSize)} / {fmtSize(totalAllowedSize)}</span>
                    </div>
                    <Progress value={usagePercentage} className="h-2 [&>div]:bg-[#E8175D]" />
                  </div>
                )}

                {!editMessageId && (
                  <div className="mt-6 pt-4 border-t border-[#E7E5E4]">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-[#78716C]">{t("create.messageLine")}</span><span className="text-[#1C1917]">{basePrice} €</span></div>
                      {extraSpaceUnits > 0 && (
                        <div className="flex justify-between text-sm"><span className="text-[#78716C]">{t("create.extraStorage", { size: extraSpaceUnits * 100 })}</span><span className="text-[#1C1917]">+{extraSpaceCost} €</span></div>
                      )}
                      <div className="flex justify-between font-semibold pt-2 border-t border-[#E7E5E4]">
                        <span className="text-[#1C1917]">{t("create.total")}</span>
                        <span className="text-[#E8175D] text-lg">{totalPrice} €</span>
                      </div>
                    </div>
                  </div>
                )}

                {editMessageId && (isUpgrade || isDowngrade) && (
                  <div className="mt-6 pt-4 border-t border-[#E7E5E4]" data-testid="upgrade-info">
                    {isUpgrade && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#78716C]">{t("create.alreadyPaid")}</span>
                          <span className="text-[#1C1917]">{amountPaid} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#78716C]">{t("create.newTierPrice")}</span>
                          <span className="text-[#1C1917]">{basePrice} €</span>
                        </div>
                        {extraSpaceUnits > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#78716C]">{t("create.extraStorage", { size: extraSpaceUnits * 100 })}</span>
                            <span className="text-[#1C1917]">+{extraSpaceCost} €</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold pt-2 border-t border-[#E7E5E4]">
                          <span className="text-[#1C1917]">{t("create.upgradeToPay")}</span>
                          <span className="text-[#E8175D] text-lg">{upgradeDiff + extraSpaceCost} €</span>
                        </div>
                      </div>
                    )}
                    {isDowngrade && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="downgrade-notice">
                        <p className="font-medium mb-1">{t("create.downgradeTitle")}</p>
                        <p>{t("create.downgradeDesc", { amount: amountPaid })}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {needsSealConfirmation && (
                <div className="space-y-4" data-testid="seal-warning-block">
                  <div className="bg-[#FDF0F4] border border-[#F0E8EB] rounded-xl p-5">
                    <p className="font-semibold text-[#1C1917] mb-2">{t("create.sealWarningTitle")}</p>
                    <p className="text-sm text-[#78716C] mb-2">{t("create.sealWarningNoModify")}</p>
                    <ul className="text-sm text-[#78716C] list-disc pl-5 space-y-0.5 mb-3">
                      <li>{t("create.sealWarningList1")}</li>
                      <li>{t("create.sealWarningList2")}</li>
                      <li>{t("create.sealWarningList3")}</li>
                    </ul>
                    <p className="text-sm text-[#78716C]">{t("create.sealWarningCanStill")}</p>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="seal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={sealAccepted}
                      onChange={(e) => setSealAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-[#D6D3D1] text-[#E8175D] focus:ring-[#E8175D] accent-[#E8175D]"
                      data-testid="seal-checkbox"
                    />
                    <span className="text-sm text-[#1C1917] leading-snug">{isReservedEdit ? t("create.sealCheckboxReserved") : t("create.sealCheckbox")}</span>
                  </label>
                </div>
              )}

              {isAdmin && (
                <div className="bg-[#FFF8E1] border border-[#F0D77A] rounded-2xl p-4 mb-4" data-testid="admin-gift-toggle-block">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={giftAsAdmin}
                      onChange={(e) => setGiftAsAdmin(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded border-[#D6D3D1] text-[#E8175D] focus:ring-[#E8175D] accent-[#E8175D]"
                      data-testid="admin-gift-checkbox"
                    />
                    <span className="text-sm text-[#1c1917] leading-snug">
                      <span className="font-semibold">🎁 {t("create.giftAsAdmin") || "Créer en Bizou cadeau (admin)"}</span>
                      <br />
                      <span className="text-xs text-[#78716C]">{t("create.giftAsAdminHelp") || "Ce Bizou sera scellé immédiatement sans passer par Stripe. Marqué « offert » dans la base."}</span>
                    </span>
                  </label>
                </div>
              )}

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="btn-secondary flex-1" data-testid="back-step-btn">{t("create.back")}</Button>
                <Button onClick={handleNext} className="btn-primary flex-1" disabled={loading || (needsSealConfirmation && !sealAccepted)} data-testid="pay-btn">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t("create.processing")}</>
                  ) : isAdmin && giftAsAdmin ? (
                    <><span className="mr-2">🎁</span>{t("create.sealAsGift") || "Sceller en cadeau"}</>
                  ) : isReservedEdit ? (
                    <><Lock className="w-4 h-4 mr-2" />{t("create.sealOnly")}</>
                  ) : editMessageId && !isUpgrade ? (
                    <><Check className="w-5 h-5 mr-2" />{t("create.save")}</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" />{isUpgrade ? t("create.sealAndSendUpgrade", { price: upgradeDiff + extraSpaceCost }) : t("create.sealAndSend", { price: totalPrice })}</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
